import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Botão do sistema. Seis variantes, porque a hierarquia de ação precisa ser
 * visível: uma ação primária por tela (gradiente + sombra de marca), o resto
 * desce a escada até o fantasma.
 */
const variantes = cva(
  [
    'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap',
    'rounded-lg font-semibold tracking-[-0.01em] transition-all duration-150',
    'disabled:pointer-events-none disabled:opacity-45',
    'active:scale-[0.98]',
  ].join(' '),
  {
    variants: {
      variante: {
        primaria: 'gradiente-marca text-white shadow-marca hover:brightness-[1.08] hover:shadow-media',
        secundaria:
          'border border-borda bg-superficie text-texto shadow-xs hover:border-bordaForte hover:bg-superficie2',
        suave: 'bg-primaria-suave text-primaria hover:bg-primaria-suave2',
        fantasma: 'text-textoFraco hover:bg-superficie2 hover:text-texto',
        perigo: 'bg-status-divergencia text-white shadow-xs hover:brightness-110',
        link: 'text-primaria underline-offset-4 hover:underline',
      },
      tamanho: {
        // Alvo de toque >= 44px: a operação usa coletor e tablet.
        padrao: 'min-h-toque px-4 text-sm',
        pequeno: 'min-h-[36px] px-3 text-[13px]',
        micro: 'min-h-[30px] px-2.5 text-2xs',
        grande: 'min-h-[52px] px-6 text-base',
        icone: 'min-h-toque min-w-toque',
        iconePequeno: 'h-9 w-9',
      },
    },
    defaultVariants: { variante: 'secundaria', tamanho: 'padrao' },
  },
);

export interface PropsBotao
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof variantes> {
  comoFilho?: boolean | undefined;
  carregando?: boolean | undefined;
}

export const Botao = React.forwardRef<HTMLButtonElement, PropsBotao>(function Botao(
  { className, variante, tamanho, comoFilho = false, carregando = false, children, ...props },
  ref,
) {
  const Componente = comoFilho ? Slot : 'button';
  return (
    <Componente
      className={cn(variantes({ variante, tamanho }), className)}
      ref={ref}
      {...(comoFilho ? {} : { disabled: props.disabled || carregando })}
      {...props}
    >
      {comoFilho ? (
        children
      ) : (
        <>
          {carregando ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {children}
        </>
      )}
    </Componente>
  );
});

/**
 * Ação em texto no meio do conteúdo ("abrir na esteira", "montar carga",
 * "Limpar"): um chip leve com borda, que se lê como clicável. Só a cor
 * primária com sublinhado no hover parecia texto e ninguém clicava (Paulo,
 * 31/10/2026). Vale para <a>, <Link> e <button>.
 */
export const acaoTexto =
  'inline-flex items-center gap-1 rounded-md border border-primaria/30 bg-primaria-suave px-2 py-0.5 font-semibold text-primaria no-underline transition hover:border-primaria hover:bg-primaria hover:text-white';

/** Número/código numa tabela que abre o registro: sublinhado pontilhado sempre visível. */
export const codigoClicavel = 'underline decoration-dotted decoration-1 underline-offset-[3px] hover:text-primaria hover:decoration-solid';
